<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";

const textbookAlignedPoints = [
  "Essay ka central argument yeh hai ke society logon ko un ke kaam, salary aur occupational status ki bunyaad par value deti hai.",
  "Nickie McWhirter is value system ko criticize karti hain aur dikhati hain ke unemployed, low-paid, ya unpaid roles ko unfairly less important samjha jata hai.",
  "Unit ka main message yeh hai ke insani worth ko job title tak reduce nahi karna chahiye.",
];

const chapterCoverage = [
  "Occupation and social status",
  "Title meaning and irony",
  "Writer's tone and critique",
  "The word `just` and social devaluation",
  "Unpaid and low-paid work ki dignity",
  "Language and social thinking",
  "Classless society claim ki challenge",
];

const quickRevisionPoints = [
  "Essay occupation-based judgment ko unfair value system ke tor par criticize karti hai.",
  "Title society ke flawed belief ko expose karta hai, endorse nahi.",
  "`Just a housewife` jese phrases dignity ko reduce kar dete hain.",
  "Language repeated labels ke through social thinking ko shape karti hai.",
  "Writer hidden class hierarchy ko expose karti hai.",
  "Main message: insani worth job title ya salary se zyada hoti hai.",
];

const readingQuestions = [
  {
    question: "What is the theme of the essay What You Do is What You Are?",
    answer: "Essay ka theme societal judgment based on occupation hai. Writer batati hai ke log aksar kisi shakhs ki value us ke profession, income aur paid work ki bunyaad par decide karte hain, jo ek unfair attitude hai.",
  },
  {
    question: "What is McWhirter's tone? Illustrate it with a few words and phrases that establish the tone.",
    answer: "McWhirter ka tone serious, critical aur reflective hai. Notes aur chapter discussion me retired teacher, laid-off worker, just a housewife aur valued from valueless jese phrases is critical tone ko support karte hain.",
  },
  {
    question: "What does the title What You Do is What You Are suggest?",
    answer: "Title society ke us flawed mindset ki taraf ishara karta hai jahan log samajhte hain ke aap ka kaam hi aap ki poori identity hai. Essay is idea ko accept nahi karti, balkeh us par tanqeed karti hai.",
  },
  {
    question: "How are unemployed people or low-paid workers generally treated in society according to the essay?",
    answer: "Essay ke mutabiq aise logon ko aksar kam ahmiyat di jati hai, unhein respect kam milti hai, aur un ke contribution ko ignore kiya jata hai. Yeh treatment society ke biased value system ko expose karta hai.",
  },
  {
    question: "Why does the essay treat unpaid work as socially important?",
    answer: "Essay yeh point banati hai ke unpaid work bhi family aur society ko support karta hai, is liye us ki dignity kam nahi hoti. Sirf salary ki absence ki wajah se kisi role ko valueless samajhna social bias hai.",
  },
  {
    question: "What is the significance of the word just in expressions like just a housewife or just a janitor?",
    answer: "Word just kisi role ko chhota, ordinary ya less valuable bana deta hai. Writer ke nazdeek yeh zaban ka aisa use hai jo logon ki dignity ko kam karta hai aur un ke kaam ko underestimate karta hai.",
  },
  {
    question: "Why does the author believe language influences our thoughts about people?",
    answer: "Author ke mutabiq hum jis language me logon ko describe karte hain, wahi language un ke bare me hamari thinking shape karti hai. Agar hum repeatedly lower-status labels use karen to society bhi un logon ko kam important samajhne lagti hai.",
  },
  {
    question: "How does the essay challenge the idea that society is classless?",
    answer: "Essay yeh dikhati hai ke chahe society apne aap ko classless kahe, practical level par woh jobs aur salaries ke hisab se logon ko rank karti rehti hai. Is liye hidden social hierarchy phir bhi mojood rehti hai.",
  },
];

const mcqs = [
  {
    question: "What is the essay mainly criticizing?",
    options: [
      { label: "a", text: "the idea that every job has equal dignity", verdict: "incorrect", reason: "Essay to exactly unequal judgment ko criticize karti hai, equal dignity ko nahi." },
      { label: "b", text: "society's habit of judging people by occupation and income", verdict: "correct", reason: "Yahi essay ka central argument hai: insani worth ko job status se measure karna unfair hai." },
      { label: "c", text: "all kinds of paid work", verdict: "incorrect", reason: "Writer paid work ko reject nahi karti; woh biased value system ko reject karti hai." },
      { label: "d", text: "the need for social respect", verdict: "incorrect", reason: "Essay respect ki importance ko support karti hai, oppose nahi." },
    ],
  },
  {
    question: "What is the effect of the word 'just' in expressions like 'just a housewife'?",
    options: [
      { label: "a", text: "It adds honor and prestige to the role.", verdict: "incorrect", reason: "Word `just` role ko elevate nahi karta; usay lower bana deta hai." },
      { label: "b", text: "It makes the role seem less valuable.", verdict: "correct", reason: "Essay ka point yahi hai ke language dignity ko reduce kar sakti hai." },
      { label: "c", text: "It proves the role is unpaid.", verdict: "incorrect", reason: "Unpaid hona aur linguistically devalue karna alag cheezen hain." },
      { label: "d", text: "It removes all social difference.", verdict: "incorrect", reason: "Actually yeh hidden hierarchy ko aur visible banata hai." },
    ],
  },
  {
    question: "According to the essay, language influences society because it:",
    options: [
      { label: "a", text: "shapes how people think about others", verdict: "correct", reason: "Writer ke mutabiq repeated labels aur phrases hamari social thinking ko form karte hain." },
      { label: "b", text: "has no connection with class or status", verdict: "incorrect", reason: "Essay isi connection ko expose karti hai." },
      { label: "c", text: "only matters in formal speeches", verdict: "incorrect", reason: "Discussion daily expressions aur ordinary labels tak phaili hui hai." },
      { label: "d", text: "automatically solves inequality", verdict: "incorrect", reason: "Language bias ko create ya reinforce kar sakti hai; automatically solve nahi karti." },
    ],
  },
];

const answerBuilding = [
  "High-scoring answer me writer ki criticism ko clearly mention karo: society insaan ko kaam ke zariye judge karti hai.",
  "Theme aur title wale jawab me paid work, dignity, respect, aur hidden social ranking ke ideas include karo.",
  "Jahan language ka sawal aaye, just, valued, valueless aur occupational labels ki discussion se answer stronger hota hai.",
  "Agar unpaid work se related question aaye to dignity aur contribution dono ko ek sath explain karo.",
  "Classless society wale answer me hidden hierarchy aur language-based ranking zaroor mention karo.",
];

usePageSeo({
  title: "What You Do is What You Are Notes | 11th Class English Unit 7",
  description: "11th class English Unit 7 notes with theme, title explanation, language and class analysis, unpaid work discussion, reading answers and MCQs.",
  keywords: "what you do is what you are notes, 11th class english unit 7, class 11 english chapter 7, what you do is what you are short questions, class language notes",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "Prose", item: "/11th/english/prose/" },
    { name: "What You Do is What You Are", item: "/11th/english/prose/what-you-do-is-what-you-are/" },
  ],
});
</script>

<template>
  <PageContainer>
    <section class="app-page-stack">
      <header class="app-page-header">
        <p class="app-page-kicker">Unit 7</p>
        <h1 class="app-page-title">What You Do is What You Are</h1>
        <p class="app-page-intro">
          Yeh page chapter ke core theme, tone aur identity-versus-occupation discussion ko exam prep format me organize karta hai. Is unit ka main concern yeh hai ke society logon ko un ke kaam ke zariye define karti hai.
        </p>
      </header>

      <section class="english-panel-muted">
        <h2 class="app-section-heading">Textbook-Aligned Snapshot</h2>
        <ul class="app-list">
          <li v-for="point in textbookAlignedPoints" :key="point">{{ point }}</li>
        </ul>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Unit 7 Coverage Checklist</h2>
        <ul class="app-list-grid">
          <li
            v-for="item in chapterCoverage"
            :key="item"
            class="english-mini-card-muted"
          >
            {{ item }}
          </li>
        </ul>
      </section>

      <section class="english-panel-muted">
        <h2 class="app-section-heading">Quick Revision Points</h2>
        <ul class="app-list">
          <li v-for="item in quickRevisionPoints" :key="item">{{ item }}</li>
        </ul>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Reading and Critical Thinking Answers</h2>
        <div class="mt-4 space-y-4">
          <article
            v-for="item in readingQuestions"
            :key="item.question"
            class="english-inner-card-muted"
          >
            <h3 class="app-title-card">{{ item.question }}</h3>
            <p class="app-rich-text mt-3">{{ item.answer }}</p>
          </article>
        </div>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">MCQs with Option Explanation</h2>
        <div class="mt-4 space-y-4">
          <article
            v-for="item in mcqs"
            :key="item.question"
            class="english-inner-card-muted"
          >
            <h3 class="app-title-card">{{ item.question }}</h3>
            <ul class="mt-3 space-y-2 text-sm leading-6 text-slate-700 dark:text-slate-300">
              <li v-for="option in item.options" :key="option.label">
                <span class="font-medium text-slate-900">{{ option.label }}. {{ option.text }}</span>
                <span>{{ option.verdict === "correct" ? " Correct:" : " Not correct:" }} {{ option.reason }}</span>
              </li>
            </ul>
          </article>
        </div>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Answer-Building Notes</h2>
        <ul class="app-list">
          <li v-for="item in answerBuilding" :key="item">{{ item }}</li>
        </ul>
      </section>
    </section>
  </PageContainer>
</template>





